/**
 * How a postal address is written, per country.
 *
 * WHY THIS EXISTS (2026-08-04)
 * ---------------------------
 * The "Billed to" block was built as `[postal_code, city].join(' ')`, which
 * renders a US address as **"19702 Newark, Delaware"**. That is the
 * German/Austrian convention. In the US, Canada and Australia the postal code
 * follows the state; in the UK it goes on its own line; in China and Japan the
 * order is largest-unit-first. One hardcoded order is wrong for most of the
 * nine locales this product ships in.
 *
 * This matters more here than on a normal screen. The block is a preview of what
 * the invoice PDF says, and an invoice is a document a finance team files against
 * a purchase order. An address a human can still parse but a machine cannot match
 * is exactly the failure mode the whole billing-identity change exists to remove.
 *
 * SCOPE, stated honestly: this covers the ordering conventions that differ across
 * the markets we sell to, not every postal system on earth. Anything unlisted
 * falls back to the majority Latin-script form (city, state, postal), which is
 * also what Stripe's own invoice renderer does. The fallback is deliberately the
 * common case rather than "leave it unformatted".
 */

export type AddressParts = {
  name?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
};

/** Postal code precedes the city on its line. Mostly continental Europe. */
const POSTAL_BEFORE_CITY = new Set([
  'DE', 'AT', 'CH', 'FR', 'BE', 'NL', 'LU', 'ES', 'PT', 'IT', 'PL', 'CZ', 'SK',
  'HU', 'SE', 'NO', 'DK', 'FI', 'GR', 'TR', 'RU', 'UA', 'BR', 'MX',
]);

/** Postal code sits alone on the last line before the country. */
const POSTAL_OWN_LINE = new Set(['GB', 'IE']);

/**
 * Largest unit first: the state/province line comes BEFORE the street.
 * Only applied when the values are in the local script — a romanised Chinese
 * address is conventionally written in Latin order, and our tenants enter
 * whichever they use, so flipping it unconditionally would produce something
 * neither convention recognises.
 */
const LARGEST_FIRST = new Set(['CN', 'JP', 'KR', 'TW', 'HK']);

const hasCJK = (s: string) => /[\u3000-\u9fff\uac00-\ud7af\uff00-\uffef]/.test(s);

function clean(...parts: (string | null | undefined)[]): string[] {
  return parts
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter((p) => p.length > 0);
}

/**
 * The address as lines, in the order that country writes it.
 *
 * Returns lines only — no country name, no company name, no tax id. The caller
 * decides what to put around it, because the invoice preview and a shipping
 * label want different surrounding fields.
 */
export function formatAddressLines(parts: AddressParts): string[] {
  const country = (parts.country ?? '').trim().toUpperCase();
  const street = clean(parts.line1, parts.line2);
  const city = clean(parts.city)[0] ?? '';
  const state = clean(parts.state)[0] ?? '';
  const postal = clean(parts.postal_code)[0] ?? '';

  if (LARGEST_FIRST.has(country) && hasCJK([city, state, parts.line1 ?? ''].join(''))) {
    // e.g. 湖北省 武汉市 / 青菱街道 / 430065
    return clean([state, city].join(' '), ...street, postal);
  }

  if (POSTAL_OWN_LINE.has(country)) {
    // e.g. 221B Baker Street / London / NW1 6XE
    return clean(...street, city, state, postal);
  }

  if (POSTAL_BEFORE_CITY.has(country)) {
    // e.g. Musterstraße 1 / 10115 Berlin
    return clean(...street, clean(postal, city).join(' '), state);
  }

  // US / CA / AU / SG / IN and the fallback: city, state postal.
  // e.g. 131 Continental Drive / Suite 305 / Newark, Delaware 19713
  const tail = clean(city).length && clean(state, postal).length
    ? `${city}, ${clean(state, postal).join(' ')}`
    : clean(city, state, postal).join(' ');
  return clean(...street, tail);
}

/**
 * The full "Bill to" block: who, where, and the identifiers a finance team
 * matches against. Country is spelled out on its own line because an invoice
 * crossing a border needs it, and the tax id follows the address the way every
 * invoice template prints it.
 */
export function billedToBlock(parts: AddressParts & {
  tax_id?: string | null;
  email?: string | null;
}): string[] {
  return clean(
    parts.name,
    ...formatAddressLines(parts),
    parts.country,
    parts.tax_id,
    parts.email,
  );
}


/**
 * Which address fields the Organization Profile form is currently editing.
 *
 * WHY THIS IS A FUNCTION AND NOT INLINE (2026-08-04)
 * -------------------------------------------------
 * The Billing details card's preview used to read the SERVER's saved
 * `billing_preview`, so while an admin edited the headquarters address beside it,
 * the card kept saying "Your invoices will show" above the OLD address. Extracted
 * so that behaviour is pinned: inline, it regressed silently, because nothing in
 * the suite could tell whether the preview followed the draft.
 *
 * This is NOT a second copy of the backend's resolution rule. The override
 * checkbox IS the rule and it lives in the same form state as the fields; the
 * form already shows the `billing_*` inputs only when it is on. What this reads is
 * the form's own state.
 *
 * The one asymmetry is deliberate: `tax_id` has no override. One company has one
 * number, the form has one input for it, and the backend resolver agrees.
 */
export type OrgFormLike = {
  billing_address_override?: boolean | null;
  name?: string | null;
  address?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
  email?: string | null;
  tax_id?: string | null;
  billing_name?: string | null;
  billing_address?: string | null;
  billing_address_line2?: string | null;
  billing_city?: string | null;
  billing_state?: string | null;
  billing_country?: string | null;
  billing_postal_code?: string | null;
  billing_email?: string | null;
};

export function billingIdentityFromForm(
  form: OrgFormLike | null | undefined,
): AddressParts & { tax_id?: string | null; email?: string | null } {
  if (!form) return {};
  const on = Boolean(form.billing_address_override);
  return {
    name: on ? form.billing_name : form.name,
    line1: on ? form.billing_address : form.address,
    line2: on ? form.billing_address_line2 : form.address_line2,
    city: on ? form.billing_city : form.city,
    state: on ? form.billing_state : form.state,
    postal_code: on ? form.billing_postal_code : form.postal_code,
    country: on ? form.billing_country : form.country,
    email: on ? form.billing_email : form.email,
    tax_id: form.tax_id,
  };
}
